/**
 * The two discussion sections drawn under the issue table: the activity log and
 * the comment threads.
 *
 * They are siblings by design — same section heading, same per-issue rule and
 * `#N Title` heading, same reserve-the-heading-with-its-first-block rule — so
 * they share the measuring helpers below and live together, apart from the
 * page furniture and the issue table in `protocolPdf.ts`.
 */
import {
  CONTENT_WIDTH,
  MARGIN,
  MUTED,
  capLines,
  drawLines,
  drawRule,
  drawSectionHeading,
  drawText,
  drawWrapped,
  ensureSpace,
  linesPerPage,
  type PdfSheet,
} from "@/domains/pm/utils/protocolPdfSheet";
import { wrapText } from "@/domains/documents/utils/templateFormPdfRenderer";
import type { Pacer } from "@/domains/pm/utils/protocolPdfPacer";
import type {
  ProtocolDocumentComment,
  ProtocolDocumentIssue,
  ProtocolDocumentModel,
} from "@/domains/pm/utils/protocol";

const ACTIVITY_TIME_WIDTH = 93;
const ACTIVITY_LINE_HEIGHT = 12;
/** The gap under an activity line. */
const ACTIVITY_LINE_GAP = 5;
const ACTIVITY_HEADING_LINE_HEIGHT = 13;
/** An issue heading's own chrome: the rule above it (18) and the gap under it (4). */
const ACTIVITY_HEADING_CHROME = 22;
/** The "Activity" section heading: 26 above the text, 19 under it. */
const ACTIVITY_SECTION_HEIGHT = 45;

/**
 * The chrome an activity line has to leave room for above itself: the section
 * heading, and a one-line issue heading. An activity entry is drawn as one
 * unbreakable unit, so a line allowed to fill the page on its own would make
 * the heading-plus-first-line reservation taller than a page — and a
 * reservation `ensureSpace` cannot honour is the stranded heading the block
 * exists to prevent.
 */
const ACTIVITY_BLOCK_MIN_CHROME =
  ACTIVITY_SECTION_HEIGHT + ACTIVITY_HEADING_CHROME + ACTIVITY_HEADING_LINE_HEIGHT;

function activityLines(sheet: PdfSheet, textValue: string): string[] {
  const textWidth = CONTENT_WIDTH - ACTIVITY_TIME_WIDTH - 14;
  return capLines(
    wrapText(textValue, sheet.regular, 9, textWidth),
    sheet.regular,
    9,
    textWidth,
    linesPerPage(ACTIVITY_LINE_HEIGHT, ACTIVITY_LINE_GAP + ACTIVITY_BLOCK_MIN_CHROME)
  );
}

function activityLineHeight(lines: string[]): number {
  return Math.max(lines.length, 1) * ACTIVITY_LINE_HEIGHT + ACTIVITY_LINE_GAP;
}

/**
 * An issue heading and the first activity line under it, measured as one
 * block. They are reserved together because a heading is meaningless without
 * the line it introduces: reserving the heading alone leaves the following
 * `drawActivityLine` to break the page on its own account, stranding the
 * heading at the foot of the page it was drawn on.
 */
function activityBlock(
  sheet: PdfSheet,
  issue: ProtocolDocumentIssue
): { heading: string[]; height: number } {
  // The heading is measured before the space is asked for: the same long
  // title that overflows a table row would otherwise be drawn past a fixed
  // reservation, and ensureSpace can only ever add one page. Its cap is taken
  // against the first activity line's MEASURED height, not an assumed single
  // line — `ensureSpace` silently caps any reservation at one page, so a block
  // reserved taller than that is not reserved at all, and the line's own
  // ensureSpace breaks the page under a heading already drawn.
  const first = issue.activity[0];
  const firstHeight = first ? activityLineHeight(activityLines(sheet, first.text)) : 0;
  const heading = capLines(
    wrapText(`${issue.number} ${issue.title}`, sheet.bold, 10.5, CONTENT_WIDTH),
    sheet.bold,
    10.5,
    CONTENT_WIDTH,
    linesPerPage(
      ACTIVITY_HEADING_LINE_HEIGHT,
      ACTIVITY_HEADING_CHROME + ACTIVITY_SECTION_HEIGHT + firstHeight
    )
  );
  return {
    heading,
    height: ACTIVITY_HEADING_CHROME + heading.length * ACTIVITY_HEADING_LINE_HEIGHT + firstHeight,
  };
}

function drawActivityLine(sheet: PdfSheet, timestamp: string, textValue: string): void {
  const lines = activityLines(sheet, textValue);
  const height = activityLineHeight(lines);
  ensureSpace(sheet, height);
  drawText(sheet, timestamp, MARGIN + 12, sheet.y - 10, { size: 8, color: MUTED });
  lines.forEach((line, index) => {
    drawText(
      sheet,
      line,
      MARGIN + ACTIVITY_TIME_WIDTH,
      sheet.y - 10 - index * ACTIVITY_LINE_HEIGHT,
      {
        size: 9,
      }
    );
  });
  sheet.y -= height;
}

const COMMENT_AUTHOR_SIZE = 8.5;
const COMMENT_TEXT_SIZE = 9.5;
const COMMENT_LINE_HEIGHT = 12.5;
/** The gap under one comment, separating it from the next. */
const COMMENT_GAP = 6;

const COMMENT_INDENT = 14;
/** A quote is indented again, inside the reply it belongs to. */
const COMMENT_QUOTE_INDENT = 26;

function commentTextLines(sheet: PdfSheet, text: string, indent: number): string[] {
  return wrapText(text, sheet.regular, COMMENT_TEXT_SIZE, CONTENT_WIDTH - indent);
}

async function drawCommentParagraphs(
  sheet: PdfSheet,
  paragraphs: string[],
  indent: number,
  muted: boolean,
  pace: Pacer
): Promise<void> {
  for (const paragraph of paragraphs) {
    for (const line of commentTextLines(sheet, paragraph, indent)) {
      ensureSpace(sheet, COMMENT_LINE_HEIGHT);
      drawText(sheet, line, MARGIN + indent, sheet.y - COMMENT_TEXT_SIZE, {
        size: COMMENT_TEXT_SIZE,
        ...(muted ? { color: MUTED } : {}),
      });
      sheet.y -= COMMENT_LINE_HEIGHT;
      await pace();
    }
  }
}

/**
 * One comment: who and when on their own line, the words indented beneath.
 *
 * Drawn line by line rather than through `drawWrapped` for the same reason a
 * note is — that caps at a page and ellipsises, and losing the tail of what
 * somebody wrote is silent data loss in a filed record.
 */
async function drawComment(
  sheet: PdfSheet,
  comment: ProtocolDocumentComment,
  pace: Pacer
): Promise<void> {
  ensureSpace(sheet, COMMENT_LINE_HEIGHT * 2);
  drawText(
    sheet,
    `${comment.author} · ${comment.timestamp}`,
    MARGIN,
    sheet.y - COMMENT_AUTHOR_SIZE,
    {
      size: COMMENT_AUTHOR_SIZE,
      color: MUTED,
    }
  );
  sheet.y -= COMMENT_LINE_HEIGHT;
  // Muted and indented again, so a reply's quote reads as context rather than
  // as something this author wrote.
  await drawCommentParagraphs(sheet, comment.quote, COMMENT_QUOTE_INDENT, true, pace);
  await drawCommentParagraphs(sheet, comment.body, COMMENT_INDENT, false, pace);
  sheet.y -= COMMENT_GAP;
}

/**
 * What people actually said, under the table that counted it.
 *
 * Same shape as the activity log — a section heading reserved together with
 * its first block so it is never stranded, then per issue a rule and a
 * `#N Title` heading — because the two read as siblings in the document and a
 * reader who has learnt one should recognise the other.
 */
export async function drawComments(
  sheet: PdfSheet,
  model: ProtocolDocumentModel,
  pace: Pacer
): Promise<void> {
  if (!model.include.issues || !model.columns.commentCount) return;
  const withComments = model.issues.filter((issue) => issue.comments.length > 0);
  const first = withComments[0];
  const following = first
    ? activityBlock(sheet, first).height
    : wrapText(model.labels.noComments, sheet.regular, 10, CONTENT_WIDTH).length * 13;
  ensureSpace(sheet, ACTIVITY_SECTION_HEIGHT + following);
  sheet.y -= 26;
  drawSectionHeading(sheet, model.labels.comments, sheet.y);
  sheet.y -= 19;
  if (!first) {
    sheet.y -= drawWrapped(sheet, model.labels.noComments, MARGIN, sheet.y, CONTENT_WIDTH, {
      color: MUTED,
    });
    return;
  }
  for (const issue of withComments) {
    const { heading, height } = activityBlock(sheet, issue);
    ensureSpace(sheet, height);
    drawRule(sheet, sheet.y);
    sheet.y -= 18;
    const headingHeight = drawLines(sheet, heading, MARGIN, sheet.y, {
      font: sheet.bold,
      size: 10.5,
      lineHeight: ACTIVITY_HEADING_LINE_HEIGHT,
    });
    sheet.y -= headingHeight + 4;
    for (const comment of issue.comments) {
      await drawComment(sheet, comment, pace);
    }
    sheet.y -= 7;
  }
}

export async function drawActivity(
  sheet: PdfSheet,
  model: ProtocolDocumentModel,
  pace: Pacer
): Promise<void> {
  if (!model.include.activity) return;
  const withActivity = model.issues.filter((issue) => issue.activity.length > 0);
  const first = withActivity[0];
  // The section heading is reserved together with whatever follows it, for the
  // same reason an issue heading is: on its own it can be the last thing drawn
  // on a page, with the entire section it announces starting overleaf.
  const following = first
    ? activityBlock(sheet, first).height
    : wrapText(model.labels.noActivity, sheet.regular, 10, CONTENT_WIDTH).length * 13;
  ensureSpace(sheet, ACTIVITY_SECTION_HEIGHT + following);
  sheet.y -= 26;
  drawSectionHeading(sheet, model.labels.activity, sheet.y);
  sheet.y -= 19;
  if (!first) {
    sheet.y -= drawWrapped(sheet, model.labels.noActivity, MARGIN, sheet.y, CONTENT_WIDTH, {
      color: MUTED,
    });
    return;
  }
  for (const issue of withActivity) {
    const { heading, height } = activityBlock(sheet, issue);
    ensureSpace(sheet, height);
    drawRule(sheet, sheet.y);
    sheet.y -= 18;
    const headingHeight = drawLines(sheet, heading, MARGIN, sheet.y, {
      font: sheet.bold,
      size: 10.5,
      lineHeight: ACTIVITY_HEADING_LINE_HEIGHT,
    });
    sheet.y -= headingHeight + 4;
    for (const activity of issue.activity) {
      drawActivityLine(sheet, activity.timestamp, activity.text);
      await pace();
    }
    sheet.y -= 7;
  }
}
